import {PopoverProvider, PopoverDisclosure, usePopoverStore, usePopoverContext} from '@ariakit/react'
import {Button} from '@/components/ui/button/Button'
import {CalendarIcon, ChevronDownIcon} from '@/components/ui/icon/Icon'
import {
    StyledDateRangeFilterPopoverContentHeader,
    StyledDateRangeFilterPopover,
    StyledDateRangeFilterPopoverContent
} from './style'
import i18n, {useTranslation} from '@/translations/i18n'

import {useMemo, useState} from 'react'
import dayjs from 'dayjs'
import {RadioSteps} from '@/components/commons/radio-steps/RadioSteps'

import {formatLocaleDate} from '@/dayjs'
import {capitalize} from '@/utilities/helpers'
import {DateRangeQuarterSelector} from './DateRangeQuarterSelector'
import {DateRangeMonthSelector} from './DateRangeMonthSelector'
import {DateRangeSemesterSelector} from './DateRangeSemesterSelector'
import {DateRangeFilterYearSelector} from './DateRangeFilterYearSelector'
import {DateRangeFilterFooter} from './DateRangeFilterFooter'
import {DateRangeFilterEnum} from '@/types'
import {getQuarterDateRange, getQuarterFromMonth, getSemesterDateRange, getSemesterFromMonth} from './utils'

const defaultRangeOptions = [
    {
        label: i18n.t('commons:month'),
        value: 'month'
    },
    {
        label: i18n.t('commons:three_months'),
        value: 'threeMonths'
    },
    {
        label: i18n.t('commons:six_months'),
        value: 'sixMonths'
    },
    {
        label: i18n.t('commons:year'),
        value: 'year'
    }
] as const satisfies {label: string; value: DateRangeFilterEnum}[]

interface DateRangeFilterProps {
    values?: {
        range: DateRangeFilterEnum
        startDate: string
        endDate: string
    }
    onApply: (values: {startDate: string; endDate: string; range: DateRangeFilterEnum}) => void
    visibleOptions?: DateRangeFilterEnum[]
    onClear?: () => void
}

export const DateRangeFilter = ({values, onApply, visibleOptions, onClear}: DateRangeFilterProps) => {
    const {t} = useTranslation()
    const popover = usePopoverStore()
    const filteredRangeOptions = useMemo(() => {
        if (!visibleOptions) {
            return defaultRangeOptions
        }
        return defaultRangeOptions.filter(item => visibleOptions?.includes(item.value))
    }, [visibleOptions])
    const rangeSelected = useMemo(() => {
        return filteredRangeOptions.find(item => item.value === values?.range)?.label
    }, [values?.range])

    const formatRangeValue = (range: DateRangeFilterEnum, startDate: string, endDate: string): string => {
        const startDateFormatted = capitalize(formatLocaleDate(startDate, 'MMMM YYYY'))
        const endDateFormatted = capitalize(formatLocaleDate(endDate, 'MMMM YYYY'))

        if (range === 'year') {
            return dayjs(startDate).year().toString()
        }

        if (range === 'month') {
            return startDateFormatted
        }

        return `${startDateFormatted} - ${endDateFormatted}`
    }

    const rangeValueLabel = useMemo(() => {
        if (!values) {
            return t('commons:date_range_filter')
        }
        const formattedValue = formatRangeValue(values?.range, values.startDate, values.endDate)
        return `${rangeSelected}: ${formattedValue}`
    }, [rangeSelected, values?.startDate, values?.endDate, values?.range])

    return (
        <PopoverProvider store={popover} placement="bottom-start">
            <PopoverDisclosure
                render={
                    <Button variant="secondary" size="md">
                        <CalendarIcon />
                        {rangeValueLabel}
                        <ChevronDownIcon />
                    </Button>
                }
            />
            <StyledDateRangeFilterPopover unmountOnHide gutter={8}>
                <DateRangeFilterPopoverContent
                    values={values}
                    onApply={onApply}
                    rangeOptions={filteredRangeOptions}
                    onClear={onClear}
                />
            </StyledDateRangeFilterPopover>
        </PopoverProvider>
    )
}

interface DateRangeFilterPopoverContentProps extends Omit<DateRangeFilterProps, 'visibleOptions' | 'endDate'> {
    rangeOptions: {label: string; value: DateRangeFilterEnum}[]
}

export const DateRangeFilterPopoverContent = ({
    values,
    onApply,
    rangeOptions,
    onClear
}: DateRangeFilterPopoverContentProps) => {
    const [rangeType, setRangeType] = useState<DateRangeFilterEnum>(values?.range ?? rangeOptions[0].value)

    const [year, setYear] = useState(dayjs(values?.startDate).year())
    const [selectedMonth, setSelectedMonth] = useState(dayjs(values?.startDate).month())
    const [selectedSemester, setSelectedSemester] = useState(() => {
        const month = dayjs(values?.startDate).month()
        return getSemesterFromMonth(month)
    })
    const [selectedQuarter, setSelectedQuarter] = useState(() => {
        const month = dayjs(values?.startDate).month()
        return getQuarterFromMonth(month)
    })

    const popover = usePopoverContext()

    const handleApply = () => {
        const dateRangeCalculators = {
            month: () => {
                const baseDate = dayjs().set('month', selectedMonth).set('year', year)
                return {
                    startDate: baseDate.startOf('month'),
                    endDate: baseDate.endOf('month')
                }
            },
            threeMonths: () => getQuarterDateRange(selectedQuarter, year),
            sixMonths: () => getSemesterDateRange(selectedSemester, year),
            year: () => {
                const baseDate = dayjs().set('year', year)
                return {
                    startDate: baseDate.startOf('year'),
                    endDate: baseDate.endOf('year')
                }
            }
        } as const

        const calculateDateRange = dateRangeCalculators[rangeType]

        const {startDate, endDate} = calculateDateRange()

        onApply({
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
            range: rangeType
        })
        popover?.hide()
    }

    const renderSelector = () => {
        switch (rangeType) {
            case 'month':
                return <DateRangeMonthSelector selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} />
            case 'threeMonths':
                return (
                    <DateRangeQuarterSelector selectedQuarter={selectedQuarter} onQuarterChange={setSelectedQuarter} />
                )
            case 'sixMonths':
                return (
                    <DateRangeSemesterSelector
                        selectedSemester={selectedSemester}
                        onSemesterChange={setSelectedSemester}
                    />
                )
            case 'year':
                return (
                    <DateRangeMonthSelector
                        selectedMonth={selectedMonth}
                        onMonthChange={setSelectedMonth}
                        isAllYearSelected
                    />
                )
            default:
                return null
        }
    }

    return (
        <StyledDateRangeFilterPopoverContent>
            <StyledDateRangeFilterPopoverContentHeader>
                <DateRangeFilterYearSelector onYearChange={setYear} year={year} />

                <RadioSteps
                    fullWidth
                    itemWidth="100%"
                    size="md"
                    name="rangeType"
                    steps={rangeOptions}
                    value={rangeType}
                    onChange={setRangeType}
                />
            </StyledDateRangeFilterPopoverContentHeader>

            {renderSelector()}

            <DateRangeFilterFooter onApply={handleApply} onClear={onClear} />
        </StyledDateRangeFilterPopoverContent>
    )
}
